import nacl from 'tweetnacl';
import bs58 from 'bs58';

const API =
  import.meta.env.VITE_BACKEND_URL?.replace('ws://', 'http://').replace('wss://', 'https://') ??
  'http://localhost:2567';

export interface AuthState {
  wallet: string;
  token: string;
  expiresAt: number;
}

interface NonceResponse {
  nonce: string;
  message: string;
}

interface VerifyResponse {
  token: string;
  expiresIn: number;
}

// Session persistence (Camada 1): keep the auth session across a page reload so a reload no longer
// dumps the player back to the connect-wallet screen. Persisting the whole AuthState also sidesteps
// devSignIn's ephemeral keypair (identity stays stable on reload). Swapped for real SIWS + rotating
// refresh in Camada 2 — the storage shape (token + expiresAt) already fits that.
const AUTH_KEY = 'auth.v1';

export const saveAuth = (state: AuthState): void => {
  try {
    localStorage.setItem(AUTH_KEY, JSON.stringify(state));
  } catch {
    /* storage unavailable (private mode / disabled) — session just won't persist */
  }
};

export const clearAuth = (): void => {
  try {
    localStorage.removeItem(AUTH_KEY);
  } catch {
    /* empty */
  }
};

export const loadAuth = (): AuthState | null => {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(AUTH_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const state = JSON.parse(raw) as AuthState;
    if (
      typeof state.wallet !== 'string' ||
      typeof state.token !== 'string' ||
      typeof state.expiresAt !== 'number'
    ) {
      clearAuth();
      return null;
    }
    if (state.expiresAt <= Date.now()) {
      clearAuth();
      return null;
    }
    return state;
  } catch {
    clearAuth();
    return null;
  }
};

export const devSignIn = async (): Promise<AuthState> => {
  const keypair = nacl.sign.keyPair();
  const wallet = bs58.encode(keypair.publicKey);

  const nonceRes = await fetch(API + '/auth/nonce?wallet=' + encodeURIComponent(wallet));
  if (!nonceRes.ok) throw new Error('failed to get nonce');
  const { nonce, message } = (await nonceRes.json()) as NonceResponse;

  const messageBytes = new TextEncoder().encode(message);
  const signatureBytes = nacl.sign.detached(messageBytes, keypair.secretKey);
  const signature = bs58.encode(signatureBytes);

  const verifyRes = await fetch(API + '/auth/verify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ wallet, signature, nonce }),
  });
  if (!verifyRes.ok) throw new Error('authentication failed');
  const { token, expiresIn } = (await verifyRes.json()) as VerifyResponse;

  return {
    wallet,
    token,
    expiresAt: Date.now() + expiresIn * 1000,
  };
};
